/**
 * Logs page. Shows the agent activity log as a paginated table with filters
 * (ad account + date range). Renders the decision rationale and old/new values
 * so the user can audit every action the agent took.
 */
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import Card from '@/components/Cards/Card';
import Table, { type Column } from '@/components/Tables/Table';
import Input from '@/components/Forms/Input';
import Select, { type SelectOption } from '@/components/Forms/Select';
import Button from '@/components/Forms/Button';
import Pagination from '@/components/Tables/Pagination';
import Badge from '@/components/common/Badge';
import Alert from '@/components/Alerts/Alert';
import { useToast } from '@/components/Alerts/toastContext';
import * as logService from '@/services/logService';
import * as adAccountService from '@/services/adAccountService';
import type { AdAccountDTO, AgentLogDTO } from '@shared/types';
import { formatDateTime } from '@/utils/formatters';
import { logStatusMeta } from '@/pages/labels';

const PAGE_SIZE = 20;

/** Filters that have actually been applied to the query (drive fetching). */
interface AppliedFilters {
  adAccountId: string;
  startDate: string;
  endDate: string;
}

const EMPTY_FILTERS: AppliedFilters = { adAccountId: '', startDate: '', endDate: '' };

/** Agent logs view. */
export default function Logs(): ReactElement {
  const toast = useToast();

  const [logs, setLogs] = useState<AgentLogDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [adAccounts, setAdAccounts] = useState<AdAccountDTO[]>([]);
  // Draft filter inputs (edited freely; applied on demand).
  const [adAccountId, setAdAccountId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // Applied filters that actually drive the query.
  const [applied, setApplied] = useState<AppliedFilters>(EMPTY_FILTERS);

  useEffect(() => {
    adAccountService
      .list()
      .then(setAdAccounts)
      .catch(() => {
        /* non-fatal: filter simply has no ad accounts */
      });
  }, []);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const result = await logService.list({
        adAccountId: applied.adAccountId || undefined,
        startDate: applied.startDate ? new Date(applied.startDate).toISOString() : undefined,
        endDate: applied.endDate ? new Date(applied.endDate).toISOString() : undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setLogs(result.data);
      setTotal(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể tải nhật ký');
      toast.error('Không thể tải nhật ký hoạt động');
    } finally {
      setLoading(false);
    }
  }, [applied, offset, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  // Apply the draft filters and jump back to the first page.
  const applyFilters = (): void => {
    setApplied({ adAccountId, startDate, endDate });
    setOffset(0);
  };

  const resetFilters = (): void => {
    setAdAccountId('');
    setStartDate('');
    setEndDate('');
    setApplied(EMPTY_FILTERS);
    setOffset(0);
  };

  const adAccountOptions: SelectOption[] = useMemo(
    () => adAccounts.map((a) => ({ value: a.id, label: a.name ?? a.adAccountIdFb })),
    [adAccounts],
  );

  const columns: Column<AgentLogDTO>[] = [
    {
      key: 'timestamp',
      header: 'Thời gian',
      render: (row) => formatDateTime(row.timestamp),
      className: 'align-top',
    },
    { key: 'eventType', header: 'Sự kiện', className: 'align-top' },
    {
      key: 'description',
      header: 'Mô tả',
      // Wrap long text in a width-capped, break-words div. The <td> is
      // whitespace-nowrap by default, so the inner div (whitespace-normal) is
      // what makes the text wrap instead of overflowing into other columns.
      render: (row) => (
        <div className="max-w-[22rem] whitespace-normal break-words">{row.description ?? '—'}</div>
      ),
      className: 'align-top',
    },
    {
      key: 'decisionRationale',
      header: 'Lý do quyết định',
      render: (row) => (
        <div className="max-w-[26rem] whitespace-normal break-words">{row.decisionRationale ?? '—'}</div>
      ),
      className: 'align-top',
    },
    {
      key: 'change',
      header: 'Giá trị cũ → mới',
      render: (row) =>
        row.oldValue || row.newValue ? `${row.oldValue ?? '—'} → ${row.newValue ?? '—'}` : '—',
      className: 'align-top',
    },
    {
      key: 'status',
      header: 'Trạng thái',
      render: (row) => {
        const meta = logStatusMeta(row.status);
        return <Badge color={meta.color}>{meta.label}</Badge>;
      },
      className: 'align-top',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Nhật ký hoạt động</h1>
        <p className="mt-1 text-sm text-gray-500">
          Theo dõi mọi hành động và quyết định của AI Agent.
        </p>
      </div>

      {error && <Alert type="error">{error}</Alert>}

      <Card>
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Select
            label="Tài khoản quảng cáo"
            options={adAccountOptions}
            placeholder="Tất cả"
            value={adAccountId}
            onChange={(e) => setAdAccountId(e.target.value)}
          />
          <Input
            label="Từ ngày"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <Input
            label="Đến ngày"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          <div className="flex items-end gap-2">
            <Button onClick={applyFilters} className="w-full">
              Lọc
            </Button>
            <Button variant="secondary" onClick={resetFilters} className="w-full">
              Đặt lại
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          rows={logs}
          loading={loading}
          rowKey={(row) => row.id}
          emptyMessage="Chưa có nhật ký hoạt động nào."
        />

        <Pagination total={total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} />
      </Card>
    </div>
  );
}
